import React, { memo, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import Loading from 'components/App/Loading';
import { ReactComponent as NoItem } from 'assets/images/NoItem.svg';
import { LOCATIONS } from 'constants/index';
import { convertIsoDateToString } from 'utils';
import formatMoney from 'utils/formatMoney';

import {
  getBookingTimeByTab,
  getOverviewBookingsByTab,
  getStayNights,
  OVERVIEW_TAB_CONFIG,
  OVERVIEW_TABS,
} from './overviewHelpers';

import styles from './Overview.module.scss';

const renderBookingItem = (booking, index, tab) => {
  const nights = getStayNights(booking.startedAt, booking.endedAt);
  const guestCount = (booking.adults || 0) + (booking.children || 0);
  const startDate = convertIsoDateToString(booking.startedAt);
  const endDate = convertIsoDateToString(booking.endedAt);
  const paymentDate = convertIsoDateToString(booking.paidAt || booking.createdAt);
  const price = booking.totalDiscountedPrice || booking.totalPrice;
  const roomNames = booking.rooms?.map((room) => room.name).filter(Boolean).join(', ') || '-';
  const bookingTime = getBookingTimeByTab(booking, tab.key);

  return (
    <Link
      to={`${LOCATIONS.MANAGER_BOOKING}/${booking._id}`}
      className="Overview__Item"
      key={booking._id}
    >
      <div className="Overview__Item__Wrapper Overview__Item__Number">
        <span className="Overview__Item__Wrapper--Title">
          {String(index + 1).padStart(2, '0')}
        </span>
      </div>

      <div className="Overview__Item__Wrapper">
        <span className="Overview__Item__Wrapper--Title">
          {booking.userInfo?.fullName || '-'}
        </span>
        <span className="Overview__Item__Wrapper--Detail">
          {`${nights} đêm - ${guestCount} khách`}
        </span>
      </div>

      <div className="Overview__Item__Wrapper">
        <span className="Overview__Item__Wrapper--Title">{roomNames}</span>
      </div>

      <div className="Overview__Item__Wrapper">
        <span className="Overview__Item__Wrapper--Title">
          {startDate && endDate ? `${startDate} - ${endDate}` : (startDate || endDate || '-')}
        </span>
        <span className="Overview__Item__Wrapper--Detail">
          {tab.timeLabel}
          {' '}
        </span>
        <span className="Overview__Item__Wrapper--Detail">{bookingTime}</span>
      </div>

      <div className="Overview__Item__Wrapper">
        <span className="Overview__Item__Wrapper--Title Overview__Item__Wrapper--Right">
          {price ? formatMoney.format(price) : '-'}
        </span>
        <span className="Overview__Item__Wrapper--Detail Overview__Item__Wrapper--Right">
          {paymentDate ? `Thanh toán: ${paymentDate}` : '-'}
        </span>
      </div>

      <div className="Overview__Item__Wrapper">
        <span className="Overview__Item__Wrapper--Title Overview__Item__Wrapper--Id">
          {booking.userInfo?.phoneNumber || booking.orderId || '-'}
        </span>
      </div>
    </Link>
  );
};

function Overview({ placeTodayBooking, isLoading }) {
  const [tabActive, setTabActive] = useState(OVERVIEW_TABS.CHECK_IN);

  const onClickTab = useCallback((tabKey) => () => {
    setTabActive(tabKey);
  }, []);

  const bookingsByTab = useMemo(() => (
    OVERVIEW_TAB_CONFIG.reduce((acc, tab) => {
      acc[tab.key] = getOverviewBookingsByTab(placeTodayBooking, tab.key);
      return acc;
    }, {})
  ), [placeTodayBooking]);

  const tabCounts = useMemo(() => ({
    [OVERVIEW_TABS.CHECK_IN]: bookingsByTab[OVERVIEW_TABS.CHECK_IN]?.length || 0,
    [OVERVIEW_TABS.CHECK_OUT]: bookingsByTab[OVERVIEW_TABS.CHECK_OUT]?.length || 0,
    [OVERVIEW_TABS.NEW_BOOKING]: bookingsByTab[OVERVIEW_TABS.NEW_BOOKING]?.length || 0,
  }), [bookingsByTab]);

  return (
    <div className={styles.Overview}>
      <Loading loading={isLoading} loaderOnPage={false} />

      <div className="Overview">
        <span className="Overview__Title">
          Tổng quan đặt phòng hôm nay
        </span>

        <div className="Overview__Tabbar">
          {OVERVIEW_TAB_CONFIG.map((tab) => (
            <span
              key={tab.key}
              className={`Overview__Tabbar__Item ${tabActive === tab.key ? 'Overview__Tabbar__Item--Active' : ''}`}
              onClick={onClickTab(tab.key)}
              tabIndex={-1}
              role="button"
              onKeyPress={null}
            >
              {`${tab.label} (${tabCounts[tab.key]})`}
            </span>
          ))}
        </div>

        {OVERVIEW_TAB_CONFIG.map((tab) => {
          const bookings = bookingsByTab[tab.key] || [];
          const isActive = tabActive === tab.key;

          if (!isActive && bookings.length === 0) return null;

          return (
            <div
              key={tab.key}
              className={`Overview__Panel ${isActive ? 'Overview__Panel--Active' : ''}`}
            >
              {bookings.length > 0 ? (
                <div className="Overview__List">
                  {bookings.map((booking, index) => renderBookingItem(booking, index, tab))}
                </div>
              ) : (
                !isLoading && (
                  <div className="Overview__NoItem">
                    <div className="Overview__NoItem__Icon">
                      <NoItem />
                    </div>

                    <span className="Overview__NoItem__Text">
                      {tab.emptyText}
                    </span>
                  </div>
                )
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

Overview.propTypes = {
  placeTodayBooking: PropTypes.shape({
    todayCheckIn: PropTypes.arrayOf(PropTypes.shape({})),
    todayCheckOut: PropTypes.arrayOf(PropTypes.shape({})),
    todayBooking: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  isLoading: PropTypes.bool,
};

Overview.defaultProps = {
  placeTodayBooking: {
    todayCheckIn: [],
    todayCheckOut: [],
    todayBooking: [],
  },
  isLoading: false,
};

export default memo(Overview);
